import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminDownloadPaperUrl,
  adminOpenPaperPdf,
  adminPapers,
  adminUpdatePaper,
  type ConferencePaper,
} from "../api";
import { formatDateTime, paperLanguageLabel, paperStatusLabel } from "../format";
import { PageHeader } from "../shells/PageHeader";
import { PaginationBar, useClientPagination } from "../shells/pagination";

export function AdminPapersPage() {
  const { t, locale } = useLanguage();
  const [rows, setRows] = useState<ConferencePaper[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  const reload = () => adminPapers().then(setRows);
  useEffect(() => {
    reload().catch(() => setRows([]));
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status === "published-accepted") {
        if (!(r.status === "published" || (r.status === "accepted" && r.is_public))) return false;
      } else if (status !== "all" && r.status !== status) {
        return false;
      }
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      const label = paperStatusLabel(r.status, r.is_public, t);
      return [r.title, r.authors, r.language, label, paperLanguageLabel(r.language, t)].some((v) =>
        String(v).toLowerCase().includes(needle),
      );
    });
  }, [rows, q, status, t]);

  const pager = useClientPagination(filtered, 25);

  return (
    <div>
      <PageHeader title={t.admin.papers} lead={t.admin.papersLead} backTo="/admin" />

      <div className="dn-table-toolbar">
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            pager.setPage(1);
          }}
          placeholder={t.admin.search}
          aria-label={t.admin.search}
          style={{ flex: "1 1 16rem", minWidth: "12rem" }}
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            pager.setPage(1);
          }}
          aria-label={t.admin.colStatus}
        >
          <option value="all">{t.admin.filterAll}</option>
          <option value="submitted">{t.status.submitted}</option>
          <option value="under_review">{t.status.under_review}</option>
          <option value="minor_revision">{t.status.minor_revision}</option>
          <option value="major_revision">{t.status.major_revision}</option>
          <option value="revised">{t.status.revised}</option>
          <option value="accepted">{t.status.accepted}</option>
          <option value="rejected">{t.status.rejected}</option>
          <option value="published-accepted">{t.status.publishedAccepted}</option>
        </select>
      </div>
      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.colTitle}</th>
              <th>{t.admin.colAuthor}</th>
              <th>{t.admin.colLanguage}</th>
              <th>{t.admin.colSubmitted}</th>
              <th>{t.admin.colStatus}</th>
              <th>{t.admin.colFile}</th>
              <th className="dn-col-actions">{t.admin.colWorkflow}</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((p) => (
              <tr key={p.id}>
                <td data-label={t.admin.colTitle}>{p.title}</td>
                <td data-label={t.admin.colAuthor}>{p.authors}</td>
                <td data-label={t.admin.colLanguage}>{paperLanguageLabel(p.language, t)}</td>
                <td data-label={t.admin.colSubmitted}>{formatDateTime(p.created_at, locale)}</td>
                <td data-label={t.admin.colStatus}>{paperStatusLabel(p.status, p.is_public, t)}</td>
                <td data-label={t.admin.colFile}>
                  <div className="dn-cta-row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
                    {p.original_filename ? (
                      <button
                        type="button"
                        className="dn-btn dn-btn-ghost"
                        onClick={async () => {
                          const { download_url } = await adminDownloadPaperUrl(p.id);
                          window.open(download_url, "_blank", "noopener,noreferrer");
                        }}
                      >
                        {p.original_filename}
                      </button>
                    ) : null}
                    {p.has_pdf ? (
                      <button
                        type="button"
                        className="dn-btn dn-btn-ghost"
                        onClick={async () => {
                          const blob = await adminOpenPaperPdf(p.id);
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank", "noopener,noreferrer");
                          window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
                        }}
                      >
                        {t.admin.viewPdf}
                      </button>
                    ) : null}
                    {!p.original_filename && !p.has_pdf ? "—" : null}
                  </div>
                </td>
                <td className="dn-col-actions" data-label={t.admin.colWorkflow}>
                  <div className="dn-cta-row" style={{ gap: "0.4rem" }}>
                    <select
                      value={p.status === "published" ? "accepted" : p.status}
                      disabled={p.status === "published"}
                      onChange={async (e) => {
                        await adminUpdatePaper(p.id, { status: e.target.value });
                        await reload();
                      }}
                    >
                      <option value="submitted">{t.status.submitted}</option>
                      <option value="under_review">{t.status.under_review}</option>
                      <option value="minor_revision">{t.status.minor_revision}</option>
                      <option value="major_revision">{t.status.major_revision}</option>
                      <option value="revised">{t.status.revised}</option>
                      <option value="accepted">{t.status.accepted}</option>
                      <option value="rejected">{t.status.rejected}</option>
                    </select>
                    {(p.status === "accepted" || p.status === "published") && (
                      <Link className="dn-btn dn-btn-primary" to={`/admin/papers/${p.id}/publish`}>
                        {p.status === "published" ? t.admin.editPublication : t.admin.preparePublish}
                      </Link>
                    )}
                    <Link className="dn-btn dn-btn-ghost" to={`/admin/papers/${p.id}/review-decision`}>
                      {t.admin.reviewDecision}
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar
        page={pager.page}
        pages={pager.pages}
        from={pager.from}
        to={pager.to}
        total={pager.total}
        pageSize={pager.pageSize}
        onPage={pager.setPage}
        onPageSize={pager.setPageSize}
      />
    </div>
  );
}
